import { useEffect, useState } from 'react';
import { LOCALES, LOCALE_LABEL } from '@oxyflows/i18n';
import { ApiError, NetworkError, api } from '../api';
import { useI18n } from '../i18n';
import { Field, Spinner } from '../components';
import { initials as teamInitials } from '../App';
import { FlowLine, Logo, XMark } from '../brand';

/**
 * The public clinic page at /c/{slug} (spec §9).
 *
 * Everything here is visible to anyone, which shapes what it may contain:
 * published information only, free slots only, no patient lookup, and a
 * booking that is a REQUEST the clinic confirms — the default the clinic would
 * want if nobody thought about it.
 *
 * CHOOSING A DENTIST. Most people booking dental care want a particular person,
 * often the one who treated them last. So when the clinic has published its
 * team — which is already telling visitors who works there — the slots say whose
 * they are and the visitor can filter to one dentist. A clinic that has NOT
 * published its team keeps the stricter behaviour: one entry per free time, so
 * a visitor learns that 10:00 is available and never who is free at 10:00.
 */
export function PublicClinic({ slug }: { slug: string }) {
  const { t, locale, setLocale, formatTime } = useI18n();
  const [data, setData] = useState<Awaited<ReturnType<typeof api.publicClinic>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [date, setDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [slots, setSlots] = useState<{ startsAt: string; membershipId: string; dentistName: string | null }[]>([]);
  const [dentistOptions, setDentistOptions] = useState<{ membershipId: string; name: string }[]>([]);
  const [dentistFilter, setDentistFilter] = useState('');
  const [chosen, setChosen] = useState<{ startsAt: string; membershipId: string; dentistName: string | null } | null>(
    null,
  );
  const [form, setForm] = useState({ name: '', phone: '', reason: '', consent: false });
  const [otherReason, setOtherReason] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  useEffect(() => {
    api
      .publicClinic(slug)
      .then(setData)
      .catch((e) => setError(e instanceof ApiError ? e.message : e instanceof NetworkError ? t.errors.network : t.common.error));
  }, [slug, t]);

  useEffect(() => {
    if (!data || data.clinic.bookingMode === 'disabled') return;
    setChosen(null);
    api
      .publicAvailability(slug, date)
      .then((r) => {
        setSlots(r.slots);
        setDentistOptions(r.dentists ?? []);
        // A dentist chosen yesterday who is not working today would otherwise
        // filter every slot away and look like "no availability".
        setDentistFilter((f) => (r.dentists?.some((d) => d.membershipId === f) ? f : ''));
      })
      .catch(() => {
        setSlots([]);
        setDentistOptions([]);
      });
  }, [slug, date, data]);

  async function book() {
    if (!chosen) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.publicBook(slug, {
        name: form.name,
        phone: form.phone,
        startsAt: chosen.startsAt,
        membershipId: chosen.membershipId,
        reason: form.reason || undefined,
        consent: true,
      });
      setResult(r.message);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t.common.error);
    } finally {
      setBusy(false);
    }
  }

  if (error && !data) {
    return (
      <div className="public-shell">
        <div className="public-card" style={{ maxWidth: 460, margin: '80px auto' }}>
          <h1>{t.errors.notFound}</h1>
          <p style={{ color: 'var(--ink-2)' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="public-shell">
        <div style={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
          <Spinner dark />
        </div>
      </div>
    );
  }

  const visibleSlots = dentistFilter ? slots.filter((s) => s.membershipId === dentistFilter) : slots;
  const name = locale === 'ar' ? data.clinic.nameAr : data.clinic.nameEn;
  const description = locale === 'ar' ? data.clinic.descriptionAr : data.clinic.descriptionEn;
  const weekdays =
    locale === 'ar'
      ? ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت']
      : ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  return (
    <div className="public-shell">
      <header className="public-hero">
        <XMark className="public-hero-x" />
        <FlowLine className="public-hero-line" />
        <div className="public-inner">
          <div className="row" style={{ justifyContent: 'space-between', marginBottom: 32 }}>
            <span style={{ fontWeight: 600 }}>{name}</span>
            <div className="row" style={{ gap: 4 }}>
              {LOCALES.map((l) => (
                <button
                  key={l}
                  type="button"
                  className={l === locale ? 'btn btn-sm' : 'btn btn-ghost btn-sm'}
                  onClick={() => setLocale(l)}
                  aria-pressed={l === locale}
                >
                  {LOCALE_LABEL[l]}
                </button>
              ))}
            </div>
          </div>
          <h1 style={{ fontSize: 34, maxWidth: '16em' }}>{name}</h1>
          {description ? <p style={{ maxWidth: '42em', marginTop: 14, fontSize: 16 }}>{description}</p> : null}
          <div className="row" style={{ gap: 18, marginTop: 22, flexWrap: 'wrap' }}>
            {data.clinic.phone ? (
              <a className="public-contact ltr-value" href={`tel:${data.clinic.phone}`}>
                {data.clinic.phone}
              </a>
            ) : null}
            {data.clinic.address ? <span className="public-contact">{data.clinic.address}</span> : null}
          </div>
        </div>
      </header>

      <main className="public-inner public-body">
        <div className="public-grid">
          <section>
            {/*
              The full service list used to be printed here as a grid of
              fourteen cards, which is the longest and least interesting thing
              on the page. Someone booking wants to know WHO they will see and
              WHEN — the list of procedures is more useful as the reason
              dropdown in the booking form, which is where it now lives.

              Still no prices: publishing a price list is a commercial decision
              the clinic has not been asked to make.
            */}
            {data.team.length > 0 ? (
              <>
                <h2 className="public-h2">{locale === 'ar' ? 'فريقنا' : 'Our team'}</h2>
                <div className="team-list">
                  {data.team.map((m) => (
                    <div key={m.membershipId} className="team-member">
                      <span className="avatar lg">{teamInitials(m.name)}</span>
                      <div>
                        <strong>{m.name}</strong>
                        {m.title ? <span>{m.title}</span> : null}
                      </div>
                      {/* Booking with this person specifically, from the place
                          where you just read their name. */}
                      {data.clinic.bookingMode !== 'disabled' && data.canChooseDentist ? (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => {
                            setDentistFilter(m.membershipId);
                            setChosen(null);
                            document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
                          }}
                        >
                          {locale === 'ar' ? 'احجز' : 'Book'}
                        </button>
                      ) : null}
                    </div>
                  ))}
                </div>
              </>
            ) : null}

            {data.clinic.openingHours.length > 0 ? (
              <>
                <h2 className="public-h2" style={{ marginTop: 32 }}>
                  {locale === 'ar' ? 'ساعات العمل' : 'Opening hours'}
                </h2>
                <table className="hours-table">
                  <tbody>
                    {data.clinic.openingHours.map((h) => (
                      <tr key={h.weekday}>
                        <td>{weekdays[h.weekday]}</td>
                        <td className="ltr-value">{h.opens} – {h.closes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : null}
          </section>

          <aside>
            <div className="public-card" id="booking">
              <h2 className="public-h2" style={{ marginTop: 0 }}>
                {locale === 'ar' ? 'احجز موعداً' : 'Book an appointment'}
              </h2>

              {data.clinic.bookingMode === 'disabled' ? (
                <p style={{ color: 'var(--ink-2)' }}>
                  {locale === 'ar'
                    ? 'الحجز الإلكتروني غير متاح حالياً. يرجى الاتصال بالعيادة.'
                    : 'Online booking is not available. Please call the clinic.'}
                </p>
              ) : result ? (
                <div className="notice" role="status">
                  <span>{result}</span>
                </div>
              ) : (
                <div className="stack">
                  {error ? <div className="notice danger" role="alert">{error}</div> : null}

                  <Field label={locale === 'ar' ? 'التاريخ' : 'Date'}>
                    <input
                      type="date"
                      value={date}
                      min={new Date().toISOString().slice(0, 10)}
                      onChange={(e) => setDate(e.target.value)}
                      className="ltr-value"
                    />
                  </Field>

                  {dentistOptions.length > 1 ? (
                    <Field label={locale === 'ar' ? 'الطبيب' : 'Dentist'}>
                      <select
                        value={dentistFilter}
                        onChange={(e) => {
                          setDentistFilter(e.target.value);
                          setChosen(null);
                        }}
                      >
                        <option value="">{locale === 'ar' ? 'أي طبيب' : 'Any dentist'}</option>
                        {dentistOptions.map((d) => (
                          <option key={d.membershipId} value={d.membershipId}>
                            {d.name}
                          </option>
                        ))}
                      </select>
                    </Field>
                  ) : null}

                  <div>
                    <div className="section-label">
                      {locale === 'ar' ? 'الأوقات المتاحة' : 'Available times'}
                    </div>
                    {visibleSlots.length === 0 ? (
                      <p style={{ color: 'var(--ink-3)', marginTop: 6 }}>
                        {locale === 'ar' ? 'لا توجد أوقات متاحة في هذا اليوم.' : 'No times available on this day.'}
                      </p>
                    ) : (
                      <div className="slot-grid">
                        {/* Only FREE slots are ever returned. A visitor never
                            learns that a time is TAKEN, let alone by whom —
                            showing a dentist's free times says nothing about
                            their booked ones. */}
                        {visibleSlots.map((s) => {
                          const selected =
                            chosen?.startsAt === s.startsAt && chosen?.membershipId === s.membershipId;
                          return (
                            <button
                              key={`${s.startsAt}-${s.membershipId}`}
                              type="button"
                              className={`slot${selected ? ' selected' : ''}`}
                              onClick={() => setChosen(s)}
                              aria-pressed={selected}
                            >
                              <strong>{formatTime(s.startsAt, data.clinic.timezone)}</strong>
                              {/* Whose slot it is, when the visitor has not
                                  already narrowed to one person. */}
                              {s.dentistName && !dentistFilter ? <small>{s.dentistName}</small> : null}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {chosen ? (
                    <>
                      {chosen.dentistName ? (
                        <div className="notice">
                          <span>
                            {formatTime(chosen.startsAt, data.clinic.timezone)} · {chosen.dentistName}
                          </span>
                        </div>
                      ) : null}
                      <Field label={locale === 'ar' ? 'الاسم' : 'Your name'} required>
                        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                      </Field>
                      <Field label={locale === 'ar' ? 'رقم الهاتف' : 'Phone number'} required>
                        <input
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          className="ltr-value"
                          inputMode="tel"
                        />
                      </Field>
                      {/*
                        The clinic's own service list, with a general
                        consultation as the default and room to say something
                        it does not cover. A free-text box asks a patient to
                        guess what the clinic calls things; a closed list tells
                        them they cannot come for anything else.
                      */}
                      <Field label={locale === 'ar' ? 'سبب الزيارة' : 'Reason for your visit'}>
                        {otherReason ? (
                          <input
                            value={form.reason}
                            onChange={(e) => setForm({ ...form, reason: e.target.value })}
                            placeholder={locale === 'ar' ? 'صِف سبب زيارتك' : 'Tell us briefly'}
                            autoFocus
                          />
                        ) : (
                          <select
                            value={form.reason}
                            onChange={(e) => {
                              if (e.target.value === '__other') {
                                setOtherReason(true);
                                setForm({ ...form, reason: '' });
                              } else {
                                setForm({ ...form, reason: e.target.value });
                              }
                            }}
                          >
                            <option value="">
                              {locale === 'ar' ? 'استشارة عامة' : 'General consultation'}
                            </option>
                            {data.services.map((service) => (
                              <option
                                key={service.id}
                                value={locale === 'ar' ? service.nameAr : service.nameEn}
                              >
                                {locale === 'ar' ? service.nameAr : service.nameEn}
                              </option>
                            ))}
                            <option value="__other">{locale === 'ar' ? 'سبب آخر…' : 'Something else…'}</option>
                          </select>
                        )}
                      </Field>

                      {/* Explicit consent, and only the minimum data asked for. */}
                      <label className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
                        <input
                          type="checkbox"
                          checked={form.consent}
                          onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                          style={{ marginTop: 3 }}
                        />
                        <span style={{ fontSize: 13 }}>
                          {locale === 'ar'
                            ? 'أوافق على أن تتواصل معي العيادة بخصوص هذا الطلب.'
                            : 'I agree that the clinic may contact me about this request.'}
                        </span>
                      </label>

                      <button
                        type="button"
                        className="btn"
                        onClick={() => void book()}
                        disabled={busy || !form.name || !form.phone || !form.consent}
                      >
                        {busy ? <><Spinner /> …</> : locale === 'ar' ? 'إرسال الطلب' : 'Send request'}
                      </button>

                      <p style={{ fontSize: 12, color: 'var(--ink-3)', margin: 0 }}>
                        {data.clinic.bookingMode === 'request'
                          ? locale === 'ar'
                            ? 'سيتواصل معك الموظفون لتأكيد الموعد.'
                            : 'Staff will contact you to confirm the appointment.'
                          : locale === 'ar'
                            ? 'سيتم تأكيد موعدك مباشرة.'
                            : 'Your appointment will be confirmed immediately.'}
                      </p>
                    </>
                  ) : null}
                </div>
              )}
            </div>
          </aside>
        </div>
      </main>

      <footer className="public-footer">
        <div className="public-inner">
          <span>{name}</span>
          <span className="powered-by">
            {locale === 'ar' ? 'مدعوم من' : 'Powered by'}
            <Logo />
          </span>
        </div>
      </footer>
    </div>
  );
}
